/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { Actionable, Button, Divider, Text, View } from '@az/starc-ui';
import { SubmitFeedbackContextProvider } from '@/features/reviews';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useState, useMemo } from 'react';
import { ReviewsOverview } from '../ReviewsOverview/ReviewsOverview';
import { ReviewSummary } from '../ReviewSummary/ReviewSummary';
import { useGetReviewsInfinite } from '@/features/customReviews/api/getReviews';
import ReviewsFilter from '@/features/customReviews/components/ReviewsFilter';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { Header } from '../Header/Header';
import { useLabels } from '@/hooks/useLabels';
import { PreviewImages } from '../ReviewImages/PreviewImages/PreviewImages';
import { useProductReviewImages } from '../../hooks/useProductReviewImages';
import { useReviewFilters } from '../../context/ReviewFiltersProvider';
import { ReviewsList } from '../ReviewsList/ReviewsList';
import { NoReviews } from '../NoReviews/NoReviews';
import { useSingleReviewStatistic } from '@/features/reviews/api/getReviewStatistics';
import { scrollToCustomerReviews } from '../../utils/scrollToTitle';
import { CustomReviewsMetaData } from '../CustomReviewsMetaData/CustomReviewsMetaData';
import { useBvPixelTracking } from '../../hooks/useBvPixelTracking';
import { BazaarVoicePixel } from '../BazaarVoicePixel';

type SortAttributeType = 'MOST_RECENT' | 'MOST_HELPFUL' | 'MOST_RELEVANT';
const defaultSortAttribute: SortAttributeType = 'MOST_RECENT';

const labelMap = {
  customerReviews: 'label_reviews_customerReviews',
  loadMore: 'label_load_more',
  loading: 'label_reviews_loading',
  backToTop: 'label_back_to_top',
};

export function CustomReviews() {
  const labels = useLabels(labelMap);
  const { selectedRatings } = useReviewFilters();

  const [isVerified, setIsVerified] = useState(false);
  const [sortAttribute, setSortAttribute] = useState<SortAttributeType>(defaultSortAttribute);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: productDetailsData, isSuccess: productDetailsReqIsSuccess } = useProductDetails();
  const skuId = productDetailsReqIsSuccess ? productDetailsData.product?.itemId : null;

  const reviewImageResult = useProductReviewImages();
  const starRatingParams = useMemo(
    () => ({
      ...Object.fromEntries(selectedRatings.map((r) => [`showReviewsRated${r}Star`, true])),
      ...(isVerified && { showReviewsFromVerifiedPurchasers: true }),
    }),
    [selectedRatings, isVerified]
  );
  const reviewImageCaptions = reviewImageResult?.photoCaption;
  const reviewImageUrls = reviewImageResult?.imageUrls;
  const reviewImageReviews = reviewImageResult?.reviews;

  const imageCount = reviewImageUrls?.length || 0;
  const {
    data: reviewsData,
    totalReviews,
    fetchNextPage,
    isFetching,
    isLoading,
    hasNextPage,
  } = useGetReviewsInfinite({
    skuNumber: skuId ?? '',
    sortAttribute,
    limit: 20,
    searchText: searchTerm || undefined,
    ...starRatingParams,
  });

  const product = productDetailsData?.product;
  const skuNumber = product?.itemId ?? '';
  const { data: reviewStatistic } = useSingleReviewStatistic({
    skuNumber,
    enabled: !!skuNumber,
  });

  const hasReviews =
    reviewStatistic?.totalReviewCount && Number(reviewStatistic?.totalReviewCount) > 0;

  const { trackEvent } = useBvPixelTracking();

  function handleSortChange(value: string | object) {
    if (typeof value === 'string') {
      setSortAttribute(value as SortAttributeType);
    }
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
  }

  function handleLoadMore() {
    if (!isFetching) {
      void fetchNextPage();
      trackEvent('Customer Reviews', 'Click', 'Load More');
    }
  }

  return (
    <div id="custom-reviews-container">
      <View padding={{ s: 4, m: 0 }}>
        <CustomReviewsMetaData product={product} />
        <SubmitFeedbackContextProvider>
          <FlexGrid flexDirection="column" gap={{ md: 6 }}>
            <Header />
            {hasReviews ? (
              <>
                <ReviewsOverview />
                {imageCount > 0 && (
                  <PreviewImages
                    imagesUrls={reviewImageUrls}
                    photoCaptions={reviewImageCaptions}
                    reviews={reviewImageReviews}
                    photoMap={reviewImageResult?.photoMap}
                  />
                )}
                <ReviewSummary />
                <Divider className={styles.preFilterDivider} />
                <ReviewsFilter
                  onVerifiedFilter={setIsVerified}
                  onSortChange={handleSortChange}
                  onSearchChange={handleSearchChange}
                  initialSelectedFilters={[]}
                  initialVerifiedSelected={isVerified}
                  totalReviews={totalReviews}
                />
                <FlexGrid
                  flexDirection="column"
                  gap={{ sm: 4, md: 5 }}
                  className={styles.reviewsList}
                >
                  <ReviewsList reviews={reviewsData} isLoading={isLoading} />
                </FlexGrid>
                <View className={styles.buttonsContainer}>
                  {!isFetching && totalReviews !== 0 && hasNextPage ? (
                    <>
                      <View width={{ s: '100px', m: '150px' }}>
                        <Button className={styles.loadMore} onClick={handleLoadMore}>
                          {labels.loadMore}
                        </Button>
                      </View>
                      <View padding={3} />
                    </>
                  ) : null}
                  {totalReviews > 0 ? (
                    <View height={'42px'} justify={'center'}>
                      <Actionable onClick={scrollToCustomerReviews}>
                        <Text variant="text-link" decoration="underline" weight="medium">
                          {labels.backToTop}
                        </Text>
                      </Actionable>
                    </View>
                  ) : null}
                </View>
              </>
            ) : (
              <>
                <NoReviews />
              </>
            )}
          </FlexGrid>
        </SubmitFeedbackContextProvider>
        <BazaarVoicePixel />
        <div id="qualtrics-pdp-embedded-feedback" className={styles.qualtricsEmbeddedFeedback} />
      </View>
    </div>
  );
}
